import { OmsOrderDetail, OmsOrderPayment } from 'types/oms';
import Table, { ColumnsType } from 'antd/es/table';
import { useMemo, useState } from 'react';
import LOCALS from 'commons/locals';
import { Trans } from 'react-i18next';
import DualTimezoneTime from 'components/dual-timezone-time';
import {
  getOrderPaymentStatusLabel,
  getOrderPaymentStatusTagColor,
} from 'constant/oms-order-payment-status';
import { CRYPTO_PAY_TYPE_LIST } from 'commons';
import i18n from 'i18n';
import { Button, Card, Modal, Tag } from 'antd';
import { getTripleaPaymentDetail } from 'apis/oms';
import CopyButton from 'components/copy-button';
import JSONViewer from 'components/simple-json-viewer';
import { Bitcoin } from 'lucide-react';

type Props = {
  omsOrderDetail: OmsOrderDetail;
  stockPlace?: string;
};

const CryptoPaymentRecord = ({
  omsOrderDetail: { payList },
  stockPlace,
}: Props) => {
  const cryptoPayList = payList
    ? payList.filter(({ payType }) => {
        return [...CRYPTO_PAY_TYPE_LIST].includes(payType);
      })
    : [];

  const getCryptoAmount = (extJsonNew: OmsOrderPayment['extJsonNew']) => {
    if (!extJsonNew) {
      return '-';
    }

    try {
      const obj = JSON.parse(extJsonNew);
      const crypto_currency = obj?.notifyCallbackRes?.crypto_currency;
      const crypto_amount = obj?.notifyCallbackRes?.crypto_amount;

      if (!crypto_currency || !crypto_amount) {
        return '-';
      }

      return `${crypto_currency} ${crypto_amount}`;
    } catch (err) {}
  };

  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [detailModalData, setDetailModalData] = useState<any>();

  const columns: ColumnsType<OmsOrderPayment> = useMemo(() => {
    return [
      {
        title: <Trans i18nKey={LOCALS.payment_channel}></Trans>,
        dataIndex: 'payType',
        key: 'payType',
      },
      {
        title: <Trans i18nKey={LOCALS.pay_amount}></Trans>,
        key: 'payAmount',
        render: ({ payAmount, payCurrency }: OmsOrderPayment) => {
          return `${payCurrency} ${payAmount}`;
        },
      },
      {
        title: <Trans i18nKey={LOCALS.payment_status}></Trans>,
        dataIndex: 'payStatus',
        key: 'payStatus',
        render: (payStatus: OmsOrderPayment['payStatus']) => {
          if (!payStatus) return '-';
          const text = getOrderPaymentStatusLabel(payStatus);
          return (
            <Tag color={getOrderPaymentStatusTagColor(payStatus)}>{text}</Tag>
          );
        },
      },
      {
        title: i18n.t(LOCALS.third_party_transaction_id),
        dataIndex: 'tradeNo',
        key: 'tradeNo',
        render: (tradeNo: OmsOrderPayment['tradeNo']) => {
          return tradeNo;
        },
      },
      {
        title: i18n.t(LOCALS.payment_link),
        dataIndex: 'extJsonNew',
        key: 'hostedUrl',
        render: (extJsonNew: OmsOrderPayment['extJsonNew']) => {
          if (!extJsonNew) {
            return '-';
          }

          try {
            const obj = JSON.parse(extJsonNew);
            return (
              <CopyButton
                copyText={obj.createPaymentRes.hosted_url}
              ></CopyButton>
            );
          } catch (err) {}
        },
      },
      {
        title: i18n.t(LOCALS.cryptocurrency_amount),
        dataIndex: 'extJsonNew',
        key: 'cryptoAmount',
        render: (extJsonNew: OmsOrderPayment['extJsonNew']) => {
          return getCryptoAmount(extJsonNew);
        },
      },
      {
        title: <Trans i18nKey={LOCALS.created_time}></Trans>,
        dataIndex: 'createTime',
        key: 'createTime',
        render: (createTime: OmsOrderPayment['createTime']) => {
          return <DualTimezoneTime time={createTime} stockPlace={stockPlace} />;
        },
      },
      {
        title: <Trans i18nKey={LOCALS.payment_time}></Trans>,
        dataIndex: 'payTime',
        key: 'payTime',
        render: (payTime: OmsOrderPayment['payTime']) => {
          return <DualTimezoneTime time={payTime} stockPlace={stockPlace} />;
        },
      },
      {
        title: <Trans i18nKey={LOCALS.option}></Trans>,
        key: 'operation',
        fixed: 'right',
        render: ({ tradeNo }: OmsOrderPayment) => {
          return (
            <div>
              <Button
                type="link"
                onClick={() => {
                  tradeNo &&
                    getTripleaPaymentDetail(tradeNo).then((res) => {
                      setDetailModalData(res);
                      setDetailModalOpen(true);
                    });
                }}
              >
                <Trans i18nKey={LOCALS.view}></Trans>
              </Button>
            </div>
          );
        },
      },
    ];
  }, [stockPlace]);

  if (!cryptoPayList.length) {
    return null;
  }

  return (
    <Card bodyStyle={{ padding: '16px' }}>
      <div className="flex items-center gap-2 mb-4 text-base font-medium">
        <Bitcoin className="w-5 h-5" />
        {i18n.t(LOCALS.cryptocurrency_payment_records)}
      </div>
      <Table
        bordered
        rowKey={'id'}
        tableLayout="auto"
        scroll={{ x: 'max-content' }}
        pagination={false}
        columns={columns}
        dataSource={cryptoPayList}
        size="small"
      />

      <Modal
        open={detailModalOpen}
        onCancel={() => setDetailModalOpen(false)}
        width="800px"
      >
        <JSONViewer data={detailModalData}></JSONViewer>
      </Modal>
    </Card>
  );
};

export default CryptoPaymentRecord;
